import * as React from "react";
import { createRoot, Root } from "react-dom/client";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { DashboardController } from "../../../controllers";
import NumberFormatter from "../../../utilities/NumberFormatter";
import GenericTooltip from "../../Tooltips/GenericTooltip";
import ExpenseLegendItem from "../ExpenseLegendItem";
import PageLoader from "../../Page/PageLoader";

// Slice color for time entries without a client
const NO_CLIENT_COLOR = "#dddddd";

interface ClientSlice {
  key: string;
  name: string;
  color: string;
  total: number;
  hours: number;
}

const tooltipRoots = new WeakMap<HTMLElement, Root>();

// Client names are user input and the chart tooltip is rendered as raw HTML
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

interface IProps {
  currency: string;
  numberFormat: string;
}

const UnbilledTimeEntriesWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [clients, setClients] = React.useState<ClientSlice[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getUnbilledTimeEntries();
      setClients(
        response.clients.map((client) => ({
          key: client.contact_id ?? "no-client",
          name: client.name ?? t("UnbilledTimeEntriesWidget::No client"),
          color: client.color ?? NO_CLIENT_COLOR,
          total: Number(client.total),
          hours: Number(client.hours),
        })),
      );
    } catch (error) {
      console.error("Failed to fetch unbilled time entries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatHours = (hours: number) => {
    return t("UnbilledTimeEntriesWidget::{{hours}} hrs", {
      hours: NumberFormatter.formatNumber(numberFormat, hours),
    });
  };

  const header = (
    <div className="panel-header space-between">
      {t("UnbilledTimeEntriesWidget::Uninvoiced time entries")}
      <span className="panel-header-subtext">{t("UnbilledTimeEntriesWidget::To invoice")}</span>
    </div>
  );

  if (isLoading) {
    return (
      <div className="panel h-full flex flex-col mb-0!">
        {header}
        <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
          <PageLoader />
        </div>
      </div>
    );
  }

  const labels = clients.map((client) => client.name);
  const data = clients.map((client) => client.total);
  const backgroundColors = clients.map((client) => client.color);

  const unbilledTotal = clients.reduce((total, client) => total + client.total, 0);
  // A doughnut can't draw slices that are all zero, so fall back to the empty placeholder ring
  const hasChartData = labels.length > 0 && unbilledTotal > 0;

  return (
    <div className="panel h-full flex flex-col mb-0!">
      {header}

      <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
        <div className="flex h-full gap-6">
          <div className="relative min-h-40 min-w-0 flex-1">
            {!hasChartData && (
              <Doughnut
                data={{
                  labels: [],
                  datasets: [
                    {
                      data: [100],
                      backgroundColor: ["#9b9b9b"],
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutoutPercentage: 56,
                  legend: {
                    display: false,
                  },
                  elements: {
                    arc: {
                      borderWidth: 0,
                    },
                  },
                  tooltips: false,
                }}
              />
            )}
            {hasChartData && (
              <Doughnut
                data={{
                  labels: labels,
                  datasets: [
                    {
                      data: data,
                      backgroundColor: backgroundColors,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutoutPercentage: 56,
                  legend: {
                    display: false,
                  },
                  elements: {
                    arc: {
                      borderWidth: 0,
                    },
                  },
                  tooltips: {
                    mode: "index",
                    enabled: false,
                    callbacks: {
                      label: (tooltipItem: any, chartData: any) => {
                        const dataset = chartData.datasets[tooltipItem.datasetIndex];
                        const title = escapeHtml(chartData.labels[tooltipItem.index]);
                        const total = dataset.data[tooltipItem.index];
                        const backgroundColor = dataset.backgroundColor[tooltipItem.index];
                        const hours = formatHours(clients[tooltipItem.index].hours);

                        return `<div class='legend-item is-expense' >
                        <div class='legend-item-circle' style='background-color: ${backgroundColor};'></div>
                        <div class='legend-item-content'>
                          <span class='legend-item-title'>${title}</span>
                          <div>
                            <span style="font-size: 12px; color: #424955;">
                              ${NumberFormatter.formatCurrency(currency, numberFormat, total)} · <strong>${hours}</strong>
                            </span>
                          </div>
                        </div>
                      </div>
                      `;
                      },
                    },
                    custom: function (tooltipModel: any) {
                      const id = "unbilledTimeEntriesTooltip";
                      let tooltipEl = document.getElementById(id);

                      if (!tooltipEl) {
                        tooltipEl = document.createElement("div");
                        tooltipEl.id = id;
                        tooltipEl.className = "chart-tooltip";
                        document.body.appendChild(tooltipEl);
                      }

                      if (tooltipModel.opacity === 0) {
                        // @ts-ignore
                        tooltipEl.style.opacity = 0;
                        return;
                      }

                      tooltipEl.classList.remove("above", "below", "no-transform");
                      if (tooltipModel.yAlign) {
                        tooltipEl.classList.add(tooltipModel.yAlign);
                      } else {
                        tooltipEl.classList.add("no-transform");
                      }

                      if (tooltipModel.body) {
                        let root = tooltipRoots.get(tooltipEl);
                        if (!root) {
                          root = createRoot(tooltipEl);
                          tooltipRoots.set(tooltipEl, root);
                        }
                        root.render(<GenericTooltip tooltip={tooltipModel} />);
                      }

                      // @ts-ignore
                      const position = this._chart.canvas.getBoundingClientRect();

                      // @ts-ignore
                      tooltipEl.style.opacity = 1;
                      tooltipEl.style.position = "absolute";
                      tooltipEl.style.left = position.left + window.scrollX + tooltipModel.caretX + "px";
                      tooltipEl.style.top = position.top + window.scrollY + tooltipModel.caretY + "px";
                      tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                      tooltipEl.style.fontSize = tooltipModel.bodyFontSize + "px";
                      tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                      tooltipEl.style.padding = tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
                      tooltipEl.style.pointerEvents = "none";
                    },
                  },
                }}
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="dashboard-expenses-legend">
              <h1
                className="dashboard-expenses-total"
                dangerouslySetInnerHTML={{
                  __html: `${NumberFormatter.formatCurrency(currency, numberFormat, unbilledTotal)} <span> ${t("Dashboard::total")}</span>`,
                }}
              />
              <div className="legend-items">
                {clients.slice(0, 5).map((client) => {
                  const { key, color, name, total, hours } = client;

                  return (
                    <ExpenseLegendItem
                      key={key}
                      name={name}
                      total={String(total)}
                      color={color}
                      currency={currency}
                      numberFormat={numberFormat}
                      detail={formatHours(hours)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnbilledTimeEntriesWidget;
